import {AfterContentInit, Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {Validators}                                                from '@angular/forms';
import {BehaviorSubject, combineLatest, Subscription}              from 'rxjs';
import {FileState}                                                 from '@progress/kendo-angular-upload';
import {ApDynformsComponent}                                       from '../../ap-dynforms/ap-dynforms.component';
import {
  ApDynformsConfigFieldset
}                                                                  from '../../ap-dynforms/config/ap-dynforms-config-fieldset';
import {
  ApDynformsConfigComboBox
}                                                                  from '../../ap-dynforms/config/ap-dynforms-config-combobox';
import {ApDynformsValidator}                                       from '../../ap-dynforms/ap-dynforms-validator';
import {UploadStore}                                               from '../../stores/import/upload.store';
import {GetPermissionPipe}                                         from '../../ap-permission/pipes/get-permission.pipe';
import {FileStreamer}                                              from '../classes/FileStreamer';
import {RouterStore}                                               from '../../stores/router/router.store';
import {
  ApDynformsConfigPlaceholder
}                                                                  from '../../ap-dynforms/config/ap-dynforms-config-placeholder';
import {
  ApDynformsConfigUpload
}                                                                  from '../../ap-dynforms/config/ap-dynforms-config-upload';
import {FileInfoData, UploadFileEvent}                             from '../../ap-dynforms/types/ap-file-upload.types';
import {debounceTime, distinctUntilChanged, filter, tap}           from 'rxjs/operators';
import {DataType, DataTypes}                                       from '../types/ap-upload.types';
import {MapViewStore}                                              from '../../stores/layout/mapview.store';
import {LabResultAttachmentStore}                                  from '../../stores/dpl3/lab-result-attachment.store';
import {FileService}                                               from '../../services/common/file.service';
import {ApPermissions}                                             from 'invoker-transport';
import {UploadFileService}                                         from '../../services/common/upload-file.service';
import {CampaignYearStore}                                         from '../../stores/login/campaignyear.store';
import IGuid = System.IGuid;
import ILabResultAttachment = Data.Dpl3.ILabResultAttachment;

/**
 * Component for the upload data
 */
@Component({
  selector: 'ap-upload',
  template: `
    <ap-dynforms [caption]="caption | translate"
                 [headerIcon]="'ap-upload-icon'"
                 [fieldsets]="fieldSets$ | async">
    </ap-dynforms>`
})
export class ApUploadComponent implements OnInit, AfterContentInit, OnDestroy {
  @ViewChild(ApDynformsComponent, {static: true}) dynForm: ApDynformsComponent;
  public fieldSets$ = new BehaviorSubject<ApDynformsConfigFieldset[]>([]);
  public caption = 'UploadUpload';

  private _selectedDataType: DataTypes | undefined;
  private _dataTypeFormKey = 'DataType';
  private _subscriptions: Subscription[] = [];

  constructor(private mapViewStore: MapViewStore,
              private uploadStore: UploadStore,
              private routeStore: RouterStore,
              private campaignYearStore: CampaignYearStore,
              private labResultAttachmentStore: LabResultAttachmentStore,
              private fileService: FileService,
              private uploadFileService: UploadFileService,
              private permissionPipe: GetPermissionPipe) {
  }

  /**
   * Initializes the component by hiding map view and generating initial form configuration
   * @public
   */
  public ngOnInit(): void {
    this.mapViewStore.hideMapView();
    this.fieldSets$.next(this._generateFormConfig(this._selectedDataType, false));
  }

  /**
   * Sets up component subscriptions and loads import data set types after content initialization
   * @public
   */
  public ngAfterContentInit(): void {
    this._subscriptions.push(
      combineLatest([
        this.permissionPipe.transform(ApPermissions.EDIT_IMPORTS),
        this.dynForm.FormValues$.asObservable(),
        this.fieldSets$.asObservable(),
      ]).pipe(
        debounceTime(100),
        filter(([_, _1, fieldSets]) => fieldSets.length !== 0),
        tap(() => {
          this.dynForm.form.markAllAsTouched();
          this.dynForm.form.updateValueAndValidity();
          if (this.dynForm.form.valid) {
            this.routeStore.releaseNavigation();
          } else {
            this.routeStore.blockNavigation('Global__Unsaved_Data');
          }
        }),
        distinctUntilChanged(([_, pFormValues, _1], [_2, cFormValues, _3]) => {
          return pFormValues[this._dataTypeFormKey] === cFormValues[this._dataTypeFormKey];
        }),
        tap(([_, formValues, _1]) => {
          this._selectedDataType = formValues[this._dataTypeFormKey];
        })
      ).subscribe(([isEditImportAvailable, formValues, _]) => {
        this.fieldSets$.next(this._generateFormConfig(formValues[this._dataTypeFormKey], isEditImportAvailable));
      })
    );
    this._subscriptions.push(
      this.campaignYearStore.SelectedCampaignYear$.subscribe(() => {
        this.dynForm?.form?.get('Attachments')?.setValue([]);
      })
    );
  }

  /**
   * Cleans up subscriptions and releases navigation on component destruction
   * @public
   */
  public ngOnDestroy(): void {
    this._subscriptions.forEach(x => x?.unsubscribe());
    this.routeStore.releaseNavigation();
  }

  /**
   * Generates form configuration based on selected data type and edit permissions
   * @param selectedDataTypeId - ID of the selected data type
   * @param isEditImportAvailable - Flag indicating if edit import permission is available
   * @returns Array of form fieldset configurations
   * @private
   */
  private _generateFormConfig(selectedDataTypeId: DataTypes | undefined, isEditImportAvailable: boolean): ApDynformsConfigFieldset[] {
    return [
      new ApDynformsConfigFieldset({
        key: 'GeneralData',
        config: [
          new ApDynformsConfigComboBox({
            label: 'Global__Data_type',
            key: this._dataTypeFormKey,
            valueField: 'Id',
            textField: 'Name',
            value: selectedDataTypeId,
            options: this._dataTypeOptions(),
            disabled: !isEditImportAvailable,
            valuePrimitive: true,
            translate: true,
            validators: [
              new ApDynformsValidator({validator: Validators.required, errorKey: 'Global__Choice_Data_Type_Error'}),
            ]
          }),
          new ApDynformsConfigPlaceholder()
        ]
      }),
      new ApDynformsConfigFieldset({
        key: 'FilesToUpload',
        legend: 'FilesToUpload',
        useMaxWidth: true,
        config: [
          this._getFilesUploadConfig(this._selectedDataType, isEditImportAvailable)
        ]
      })
    ];
  }

  /**
   * Provides data type options for the combo box
   * @returns Array of DataType objects
   * @private
   */
  private _dataTypeOptions(): DataType[] {
    return [
      {
        Id: DataTypes.Logfiles,
        Name: 'N__Logfile',
      },
      {
        Id: DataTypes.LabResultFiles,
        Name: 'LabsFiles',
      }
    ];
  }

  /**
   * Gets file upload configuration based on data type and edit permissions
   * @param dataType - Value from DataTypes which is id of selected data type
   * @param isEditImportAvailable - Flag indicating if edit import permission is available
   * @returns Upload configuration object
   * @private
   */
  private _getFilesUploadConfig(dataType: DataTypes | undefined, isEditImportAvailable: boolean): ApDynformsConfigUpload {
    switch (dataType) {
      case DataTypes.Logfiles:
        return this._getApDynformsConfigUpload(!isEditImportAvailable, ['.zip', '.log', '.csv']);
      case DataTypes.LabResultFiles:
        return this._getApDynformsConfigUpload(!isEditImportAvailable, ['.pdf']);
      case undefined:
      default:
        return this._getApDynformsConfigUpload(true, []);
    }
  }

  /**
   * Creates upload configuration with specified restrictions
   * @param isDisabled - Flag indicating if upload is disabled
   * @param allowedFileExtensions - Array of allowed file extensions
   * @returns Upload configuration object
   * @private
   */
  private _getApDynformsConfigUpload(isDisabled: boolean, allowedFileExtensions: string[]): ApDynformsConfigUpload {
    return new ApDynformsConfigUpload({
      key: 'Attachments',
      validateWithForm: true,
      onlyUniqueFiles: true,
      disabled: isDisabled,
      restrictions: allowedFileExtensions,
      uploadEventHandler: this._uploadEventHandler.bind(this)
    });
  }

  /**
   * Handles file upload events by blocking navigation and initiating file upload
   * @param event - Upload file event containing file information
   * @private
   */
  private _uploadEventHandler(event: UploadFileEvent): void {
    this.routeStore.blockNavigation('Global__Import_in_Progress');
    this._uploadFile(event).then();
  }

  /**
   * Processes file upload using FileStreamer
   * @param event - Upload file event containing file information
   * @returns Promise that resolves when upload is complete
   * @private
   */
  private async _uploadFile(event: UploadFileEvent): Promise<void> {
    if (event.files.length <= 0) {
      return;
    }
    for (const fileDataInfo of event.files) {
      if (fileDataInfo.state === FileState.Uploading || fileDataInfo.state === FileState.Uploaded) {
        return;
      }
      try {
        switch (this._selectedDataType) {
          case DataTypes.Logfiles:
            this._logFilesUpload(fileDataInfo, event);
            break;
          case DataTypes.LabResultFiles:
            await this._labFilesUpload(fileDataInfo, event);
            break;
        }
      } catch (ex) {
        console.warn(ex);
        event.fileProcess.setUploadFailed(fileDataInfo.uid);
        this._releaseNavigationRouteIfReady(event.files);
      }
    }
  }

  /**
   * Handles the upload of log files using FileStreamer
   * @param fileDataInfo - Information about the file being uploaded
   * @param event - Upload event containing file processing callbacks
   * @private
   */
  private _logFilesUpload(fileDataInfo: FileInfoData, event: UploadFileEvent): void {
    const fileStreamer = new FileStreamer(fileDataInfo.rawFile, this.uploadStore, fileDataInfo.uid);
    fileStreamer.onChunkFinish.subscribe(oCF => {
      const chunkSize = Math.floor(oCF.valueOf() / fileDataInfo.rawFile.size * 100);
      event.fileProcess.setUploadProgress(fileDataInfo.uid, chunkSize);
    });
    fileStreamer.onFinish.subscribe(async () => {
      const name = fileDataInfo.name;
      this.uploadStore.verifyLogUpload(fileDataInfo.uid, name);
      event.fileProcess.setUploadProgress(fileDataInfo.uid, 100);
      event.fileProcess.setUploadFinished(fileDataInfo.uid);
      this._releaseNavigationRouteIfReady(event.files);
    });
    fileStreamer.onError.subscribe(() => {
      event.fileProcess.setUploadFailed(fileDataInfo.uid);
      this._releaseNavigationRouteIfReady(event.files);
    });
    fileStreamer.readBlockAsText();
  }

  /**
   * Handles the upload of lab result files
   * Converts a file to base64, creates an attachment object, and manages upload progress
   * @param fileDataInfo - Information about the file being uploaded
   * @param event - Upload event containing file processing callbacks
   * @returns Promise that resolves when upload is complete
   * @private
   */
  private async _labFilesUpload(fileDataInfo: FileInfoData, event: UploadFileEvent): Promise<void> {
    const rawFileDataUrl = await this.fileService.readFileToDataUrl(fileDataInfo.rawFile);
    const attachment = {
      Id: fileDataInfo.uid as IGuid,
      Name: fileDataInfo.name,
      LastModified: fileDataInfo.rawFile.lastModified,
      ContentSize: fileDataInfo.rawFile.size,
      ContentMimeType: fileDataInfo.rawFile.type,
      ContentBase64: this.fileService.getBase64FromDataUrl(rawFileDataUrl)
    } as ILabResultAttachment;
    event.fileProcess.setUploadProgress(fileDataInfo.uid, 0);
    let shouldProgressStopProgress = false;
    this.uploadFileService.controlUploadProgress((progress: number) => {
      event.fileProcess.setUploadProgress(fileDataInfo.uid, progress);
      return shouldProgressStopProgress;
    }, {Interval: 100, Increment: 9});
    this.labResultAttachmentStore.uploadLabResultAttachment(attachment)
      .then(() => {
        shouldProgressStopProgress = true;
        event.fileProcess.setUploadProgress(fileDataInfo.uid, 100);
        event.fileProcess.setUploadFinished(fileDataInfo.uid);
        this._releaseNavigationRouteIfReady(event.files);
      })
      .catch(() => {
        shouldProgressStopProgress = true;
        event.fileProcess.setUploadFailed(fileDataInfo.uid);
        this._releaseNavigationRouteIfReady(event.files);
      });
  }

  /**
   * Releases navigation route if all files have finished uploading
   * @param files - Array of file information data
   * @private
   */
  private _releaseNavigationRouteIfReady(files: FileInfoData[]): void {
    if (!files || files.length === 0 || files?.some(x => x.state === FileState.Uploading)) {
      return;
    }
    this.routeStore.releaseNavigation();
  }
}
